/**
 * What staff have typed on the checkout screen, held just long enough to walk
 * out to the service screens and back.
 *
 * Adding work to the order means leaving /checkout, and a route change throws
 * the whole form away: the location, who checked it in, the customer's name,
 * email and number, all of it (Calley, 24 Oct 2026). Those answers ride along
 * here instead.
 *
 * Two things keep one customer's answers off the next customer's order:
 *
 *  - every record is stamped with the cart it belongs to, and nothing is given
 *    back unless the cart asking for it is the same one. A new order is a new
 *    cart id (api/cart.js), so a stale record can only ever be ignored;
 *  - it lives in sessionStorage, which `resetKioskSession()` clears between
 *    customers along with everything else the browser remembers
 *    (utils/kioskSession.js).
 *
 * Nothing about money is held here. A promo code, a gift card, a line given
 * away or a waived fee is a decision, not data entry, and is made again on a
 * screen that shows what it does to the bill.
 */

const KEY = "checkoutAnswers";

export const saveCheckoutAnswers = (cart, answers) => {
  if (!cart) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ cart, answers }));
  } catch (error) {
    // A full or blocked sessionStorage costs the staff a retype, nothing more.
    console.error("[CHECKOUT] could not hold the answers:", error?.message);
  }
};

/** The answers for this cart, or null: a different cart, or none held. */
export const loadCheckoutAnswers = (cart) => {
  if (!cart) return null;
  try {
    const held = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (!held || held.cart !== cart) return null;
    return held.answers || null;
  } catch {
    return null;
  }
};

export const forgetCheckoutAnswers = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do: the kiosk reset clears the whole of sessionStorage.
  }
};
